import { fabric } from 'fabric';
import {
  alignShift, ALIGN_LABEL, DISTRIBUTE_LABEL, distributeShifts, fitPageBox, groupOf, LAYER_LABEL, layerIndex, mainColor,
  groupTargets, loneMembers, newGroupId, plainHex, regroupIds, styleOf, styleProblem, unionBox, units, wholeGroup,
  type Align, type Axis, type Box, type CopiedStyle, type LayerMove
} from '../editor/arrange';
import { EXTRA_PROPS, type Editor } from '../editor/editor';
import { arrowOverhang } from '../editor/arrows';
import type { History } from '../editor/history';
import { EDIT_MAX_SIDE, isNoEdit, readEdit } from '../editor/photo';
import { naturalSize } from '../editor/picture';
import { exportObjectsPng } from '../export/render';
import { exportFileName } from '../export/plan';
import { fontNeeds, loadFonts } from '../fonts/load';
import { effectivePpi, pxToMm, type PlainObject } from '../print/rules';
import { isThaiText } from '../thai/ThaiTextbox';
import { thStamp } from '../time';
import { checkDocSize } from './docSheet';
import { download } from './exportSheet';
import { labelOf, setLocked } from './layersSheet';
import { applyEdit } from './photoSheet';
import { toast } from './toast';

/*
 * What the object menu (src/ui/contextMenu.ts), the floating bar and the keyboard do to the chosen objects.
 * Each action returns null when done, or a Thai reason when it was not done (shown to the person, never skipped silently).
 * Copy/paste is the app's own clipboard (choice ก): it stays while the app is open, across pages and works on this device.
 */

type Obj = fabric.Object & { locked?: boolean; name?: string; photoEdit?: unknown; groupId?: string };
const errText = (e: unknown) => e instanceof Error ? e.message : String(e);

/** the app's clipboard: plain objects at their page positions (survives changing page or opening another work) */
let clip: { objects: Array<Record<string, unknown>>; at: number } | null = null;
/** คัดลอกรูปแบบ */
let style: CopiedStyle | null = null;

export function createObjectActions(ed: Editor, history: History, opts: {
  /** false on someone else's design (read-only) */
  canEdit: () => boolean;
  /** "เป็นพื้นหลัง" of the toolbar (src/ui/toolbar.ts) */
  asBackground: () => void;
  /** the design's background colour changed (saved like the colour in เพิ่มเติม) */
  onBgChanged: () => void;
  /** objects were reordered/locked (the layer list follows) */
  onArranged: () => void;
}) {
  const { canvas } = ed;
  const READ_ONLY = 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว';
  const LOADING = 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่';
  /** why the work cannot be changed right now (BUG-10: while a page loads, the old page's objects are still on the canvas) */
  const cannotEdit = () => !opts.canEdit() ? READ_ONLY : history.loading ? LOADING : null;

  /** the chosen objects (a group selection's members), or [] */
  function chosen(): Obj[] {
    const o = canvas.getActiveObject() as Obj | null;
    if (!o || (o as fabric.IText).isEditing) return [];
    return o.type === 'activeSelection' ? (o as fabric.ActiveSelection).getObjects() as Obj[] : [o];
  }
  /** run `fn` with the selection taken apart (members at their page positions), then choose them again */
  function loose<T>(list: Obj[], fn: () => T): T {
    const wasGroup = canvas.getActiveObject()?.type === 'activeSelection';
    if (wasGroup) canvas.discardActiveObject();
    try { return fn(); }
    finally {
      list.forEach(o => o.setCoords());
      if (wasGroup && list.length > 1) canvas.setActiveObject(new fabric.ActiveSelection(list, { canvas }));
      else if (list.length === 1 && list[0].selectable !== false) canvas.setActiveObject(list[0]);
      ed.paint(); canvas.requestRenderAll();
    }
  }
  const boxOf = (o: fabric.Object): Box => { o.setCoords(); const r = o.getBoundingRect(true, true); return { x: r.left, y: r.top, w: r.width, h: r.height }; };
  const plain = (o: fabric.Object) => o.toObject(EXTRA_PROPS) as Record<string, unknown>;

  /* ---------- คัดลอก / วาง / ทำสำเนา / ลบ ---------- */

  function copy(): string | null {
    const list = chosen();
    if (!list.length) return 'เลือกชิ้นงานก่อน แล้วค่อยคัดลอก';
    const objects = loose(list, () => list.map(plain));
    clip = { objects, at: 0 };
    toast(list.length > 1 ? `คัดลอก ${list.length} ชิ้นแล้ว วางได้ทุกหน้า ทุกงาน (⌘V)` : 'คัดลอกแล้ว วางได้ทุกหน้า ทุกงาน (⌘V)');
    return null;
  }

  const hasClip = () => !!clip;

  async function paste(): Promise<string | null> {
    { const no = cannotEdit(); if (no) return no; }
    if (!clip) return 'ยังไม่ได้คัดลอกอะไรในแอปนี้ (คัดลอกจากแอปอื่นยังวางไม่ได้)';
    const page = history.currentPage();
    let objects = clip.objects.map(o => structuredClone(o));
    // on a video the copies show for the whole video (their old timing may not fit this one)
    if (ed.doc.video) objects = objects.map(({ vStart: _s, vEnd: _e, vFade: _f, ...rest }) => rest);
    // copies never join the original's group: a copied group becomes a new one
    const ids = regroupIds(objects.map(groupOf));
    objects = objects.map(({ groupId: _g, ...rest }, k) => ids[k] ? { ...rest, groupId: ids[k] } : rest);
    const failed = await loadFonts(fontNeeds(objects));
    if (failed.length) toast(`โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตอนนี้แสดงด้วยฟอนต์สำรอง`, true);
    // the same spot when it is free (another page/work), a step down-right of the original when pasted over it
    const d = 24 * ed.unit();
    const over = (o: Record<string, unknown>) => ed.userObjects().some(x => x.type === o.type && Math.abs((x.left ?? 0) - Number(o.left)) < 0.5 && Math.abs((x.top ?? 0) - Number(o.top)) < 0.5);
    const shift = objects.some(over) ? d * (clip.at + 1) : 0;
    const made = await new Promise<fabric.Object[]>((res, rej) => {
      try { fabric.util.enlivenObjects(objects, (out: fabric.Object[]) => res(out.filter(Boolean)), ''); }
      catch (e) { rej(e); }
    }).catch(e => { throw new Error('วางไม่สำเร็จ: ' + errText(e)); });
    if (made.length < objects.length) return `วางได้ ${made.length} จาก ${objects.length} ชิ้น (มักเป็นรูปที่โหลดไม่ได้) จึงไม่วาง`;
    // fonts and pictures took a moment: still the same page, not loading, still mine (else they would land elsewhere or vanish)
    if (history.currentPage() !== page) return 'เปลี่ยนหน้าระหว่างกำลังวาง จึงไม่ได้วาง ลองวางอีกครั้งในหน้าที่ต้องการ';
    { const no = cannotEdit(); if (no) return no; }
    canvas.discardActiveObject();
    made.forEach(o => { o.set({ left: (o.left ?? 0) + shift, top: (o.top ?? 0) + shift }); o.setCoords(); canvas.add(o); });
    if (shift) clip.at++;
    const pick = made.filter(o => o.selectable !== false);
    if (pick.length > 1) canvas.setActiveObject(new fabric.ActiveSelection(pick, { canvas }));
    else if (pick.length) canvas.setActiveObject(pick[0]);
    ed.paint(); canvas.requestRenderAll();
    return null;
  }

  function duplicate(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const o = canvas.getActiveObject();
    if (!o || (o as fabric.IText).isEditing) return 'เลือกชิ้นงานก่อน แล้วค่อยทำสำเนา';
    o.clone((c: fabric.Object | null) => {
      if (!c) { toast('ทำสำเนาไม่สำเร็จ (มักเป็นรูปที่โหลดไม่ได้) ลองใหม่อีกครั้ง', true); return; }
      canvas.discardActiveObject();
      const d = 24 * ed.unit();
      c.set({ left: (c.left ?? 0) + d, top: (c.top ?? 0) + d, evented: true });
      // the copy never joins the original's group: a duplicated group becomes a new one
      const parts = (c.type === 'activeSelection' ? (c as fabric.ActiveSelection).getObjects() : [c]) as Obj[];
      const ids = regroupIds(parts.map(groupOf));
      parts.forEach((x, k) => { if (ids[k]) x.groupId = ids[k]!; else delete x.groupId; });
      if (c.type === 'activeSelection') {
        c.canvas = canvas;
        (c as fabric.ActiveSelection).forEachObject(x => canvas.add(x));
        c.setCoords();
      } else canvas.add(c);
      canvas.setActiveObject(c); ed.paint();
    }, EXTRA_PROPS);
    return null;
  }

  function remove(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (!list.length) return 'เลือกชิ้นงานก่อน แล้วค่อยลบ';
    list.forEach(x => canvas.remove(x));
    // a group left with one member is no group any more (else "แยกกลุ่ม" would show on a plain object)
    loneMembers(ed.userObjects() as Obj[]).forEach(o => { delete o.groupId; });
    canvas.discardActiveObject(); canvas.requestRenderAll();
    return null;
  }

  /* ---------- ล็อก ---------- */

  function lock(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (!list.length) return 'เลือกชิ้นงานก่อน แล้วค่อยล็อก';
    canvas.discardActiveObject();
    list.forEach(x => setLocked(x, true));
    canvas.requestRenderAll(); history.push(); opts.onArranged();
    toast('ล็อกแล้ว ปลดล็อกได้ที่เมนูนี้หรือแท็บเลเยอร์');
    return null;
  }
  function unlock(o: Obj): string | null {
    { const no = cannotEdit(); if (no) return no; }
    if (!canvas.getObjects().includes(o)) return 'ชิ้นนี้ไม่อยู่ในหน้านี้แล้ว (เลิกทำหรือเปลี่ยนหน้าไปแล้ว) เปิดเมนูใหม่อีกครั้ง';
    setLocked(o, false);
    canvas.setActiveObject(o); ed.paint(); canvas.requestRenderAll(); history.push(); opts.onArranged();
    toast('ปลดล็อกแล้ว');
    return null;
  }

  /* ---------- เลเยอร์ ---------- */

  function layer(how: LayerMove): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (!list.length) return 'เลือกชิ้นงานก่อน';
    if (list.length > 1 && (how === 'forward' || how === 'backward')) return `"${LAYER_LABEL[how]}" ใช้ได้ทีละชิ้น (ไปหน้าสุด/หลังสุดใช้กับหลายชิ้นได้)`;
    const stackOf = () => canvas.getObjects().map(x => ({ board: x === ed.board, locked: !!(x as Obj).locked }));
    let moved = 0, why = '';
    loose(list, () => {
      // several to the front keep their order: the lowest goes first; to the back: the highest goes first
      const order = [...list].sort((a, b) => canvas.getObjects().indexOf(a) - canvas.getObjects().indexOf(b));
      for (const o of how === 'back' ? order.reverse() : order) {
        const r = layerIndex(stackOf(), canvas.getObjects().indexOf(o), how);
        if (r.why) { why = r.why; continue; }
        canvas.moveTo(o, r.to); moved++;
      }
    });
    if (!moved) return why;
    history.push(); opts.onArranged();
    toast(`${LAYER_LABEL[how]}แล้ว`);
    return null;
  }

  /* ---------- จัดตำแหน่ง ---------- */

  function align(how: Align): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen().filter(o => !o.locked);
    if (!list.length) return 'เลือกชิ้นงานก่อน';
    // a group moves as one piece (its members keep their places in it)
    const us = units(list);
    loose(list, () => {
      // one piece: on the page (trim edge); several: with each other (the box around them)
      const target = us.length > 1 ? unionBox(list.map(boxOf)) : (() => { const p = ed.pageBox(false); return { x: p.x, y: p.y, w: p.w, h: p.h }; })();
      for (const u of us) {
        const s = alignShift(unionBox(u.map(boxOf)), target, how);
        u.forEach(o => o.set({ left: (o.left ?? 0) + s.dx, top: (o.top ?? 0) + s.dy }));
      }
    });
    history.push();
    toast(`${ALIGN_LABEL[how]}${us.length > 1 ? 'กันแล้ว' : 'บนหน้าแล้ว'}`);
    return null;
  }

  /** how many pieces are chosen (a group counts as one) — the menu says "บนหน้า" or "เทียบกันเอง" by it */
  const pieces = () => units(chosen()).length;

  /* ---------- กระจายระยะห่างเท่ากัน ---------- */

  function distribute(axis: Axis): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen().filter(o => !o.locked);
    const us = units(list);
    if (us.length < 3) return `"${DISTRIBUTE_LABEL[axis]}" ต้องเลือกอย่างน้อย 3 ชิ้น (กลุ่มนับเป็น 1 ชิ้น) ตอนนี้เลือกไว้ ${us.length} ชิ้น`;
    loose(list, () => {
      const s = distributeShifts(us.map(u => unionBox(u.map(boxOf))), axis);
      us.forEach((u, k) => u.forEach(o => o.set({ left: (o.left ?? 0) + s[k].dx, top: (o.top ?? 0) + s[k].dy })));
    });
    history.push();
    toast(`${DISTRIBUTE_LABEL[axis]}แล้ว (${us.length} ชิ้น · เลิกทำได้)`);
    return null;
  }

  /* ---------- จัดกลุ่ม / ยกเลิกการรวมกลุ่ม (selection: src/ui/groupSelect.ts) ---------- */

  const choosable = () => ed.userObjects().filter(o => o.selectable !== false && o.visible !== false) as Obj[];

  /** what the floating bar offers: รวมกลุ่ม (several, not one whole group) · แยกกลุ่ม (a group or one of its members) */
  function groupState(): 'group' | 'ungroup' | null {
    const list = chosen();
    if (list.length > 1 && !wholeGroup(list, choosable())) return 'group';
    const all = ed.userObjects() as Obj[];
    return list.some(o => { const g = groupOf(o); return !!g && all.filter(x => groupOf(x) === g).length > 1; }) ? 'ungroup' : null;
  }

  function group(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (list.some(o => o.locked)) return 'มีชิ้นที่ล็อกอยู่ ปลดล็อกก่อนแล้วค่อยจัดกลุ่ม';
    if (list.length < 2) return 'เลือกอย่างน้อย 2 ชิ้นก่อน (ลากคลุมบนที่ว่าง หรือกด Shift ค้างแล้วแตะทีละชิ้น) แล้วค่อยจัดกลุ่ม';
    if (wholeGroup(list, choosable())) return 'ชิ้นที่เลือกเป็นกลุ่มเดียวกันอยู่แล้ว';
    // groups inside the selection join the new one (one level, as in Canva) — all their members, hidden/locked ones too
    const id = newGroupId();
    const members = groupTargets(list, ed.userObjects() as Obj[]);
    members.forEach(o => { o.groupId = id; });
    history.push(); opts.onArranged(); canvas.requestRenderAll();
    toast(`จัดกลุ่ม ${members.length} ชิ้นแล้ว · แตะชิ้นไหนก็เลือกทั้งกลุ่ม แตะซ้ำเพื่อแก้ทีละชิ้น (เลิกทำได้)`);
    return null;
  }

  function ungroup(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const ids = new Set(chosen().map(groupOf).filter((g): g is string => !!g));
    if (!ids.size) return 'ชิ้นที่เลือกไม่ได้อยู่ในกลุ่ม';
    // the whole group, locked and hidden members too (else they would stay a group of their own)
    const members = ed.userObjects().filter(o => { const g = groupOf(o as Obj); return !!g && ids.has(g); }) as Obj[];
    members.forEach(o => { delete o.groupId; });
    history.push(); opts.onArranged(); canvas.requestRenderAll();
    toast(`แยกกลุ่มแล้ว (${members.length} ชิ้น) ตอนนี้เลือกและย้ายทีละชิ้นได้ (เลิกทำได้)`);
    return null;
  }

  /* ---------- คัดลอก/วาง รูปแบบ ---------- */

  function copyStyle(): string | null {
    const list = chosen();
    if (list.length !== 1) return list.length ? 'คัดลอกรูปแบบได้ทีละชิ้น' : 'เลือกชิ้นงานก่อน';
    const s = styleOf(plain(list[0]));
    if (!s) return 'ชิ้นนี้ไม่มีรูปแบบให้คัดลอก';
    style = s;
    toast('คัดลอกรูปแบบแล้ว เลือกอีกชิ้นแล้วใช้ "วางรูปแบบ"');
    return null;
  }
  const styleToPaste = () => style;

  function pasteStyle(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    if (!style) return 'ยังไม่ได้คัดลอกรูปแบบ';
    const list = chosen().filter(o => !o.locked);
    if (!list.length) return 'เลือกชิ้นงานที่จะวางรูปแบบก่อน';
    const s = style;
    const fits = list.filter(o => !styleProblem(s, o.type));
    if (!fits.length) return styleProblem(s, list[0].type);
    const problems: string[] = [];
    const { photoEdit, shadow, ...rest } = s.props as Record<string, unknown> & { photoEdit?: unknown; shadow?: unknown };
    const edit = readEdit(photoEdit);
    loose(list, () => {
      for (const o of fits) {
        // Fabric's GPU filters cut off a picture larger than 4096 px: แต่งภาพ makes it smaller first (src/ui/photoSheet.ts)
        if (o.type === 'image' && !isNoEdit(edit)) {
          const n = naturalSize(o as fabric.Image);
          if (Math.max(n.w, n.h) > EDIT_MAX_SIDE) { problems.push(`รูป "${labelOf(o) || 'รูปภาพ'}" ใหญ่ ${n.w} × ${n.h} px เกิน ${EDIT_MAX_SIDE} px กด "แต่งภาพ" ที่รูปนั้นก่อน (ระบบจะย่อให้) แล้ววางรูปแบบอีกครั้ง`); continue; }
        }
        o.set(rest as Partial<fabric.Object>);
        o.set('shadow', shadow ? new fabric.Shadow(shadow as fabric.IShadowOptions) : undefined);
        if (o.type === 'image') {
          const why = applyEdit(o as fabric.Image, edit);
          if (why) problems.push(why);
        }
        if (isThaiText(o)) o.initDimensions();
        o.dirty = true;
      }
    });
    canvas.requestRenderAll(); history.push();
    if (problems.length) return problems[0];
    toast(fits.length < list.length ? `วางรูปแบบให้ ${fits.length} ชิ้น (อีก ${list.length - fits.length} ชิ้นเป็นคนละชนิด จึงไม่ได้วาง)` : 'วางรูปแบบแล้ว (เลิกทำได้)');
    return null;
  }

  /* ---------- เป็นพื้นหลัง · ใช้สีกับพื้นหลัง ---------- */

  function asBackground(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (list.length !== 1 || list[0].type !== 'image') return '"เป็นพื้นหลัง" ใช้กับรูปภาพทีละรูป';
    if (ed.doc.video) return 'บนวิดีโอใช้ "เป็นพื้นหลัง" ไม่ได้ (รูปจะบังคลิปทั้งช่วงเวลาของมัน)';
    opts.asBackground();
    return null;
  }

  /** the object's colour: a shape's/text's fill, a line's stroke, a picture's main colour */
  function colorOf(o: Obj): string | null {
    if (o.type === 'line') return plainHex(o.stroke);
    if (o.type !== 'image') return plainHex(o.fill);
    const img = o as fabric.Image;
    const el = img.getElement() as HTMLImageElement | HTMLCanvasElement | undefined;
    const n = naturalSize(img);
    if (!el || !n.w || ((el as HTMLImageElement).complete === false)) throw new Error('รูปนี้ยังโหลดไม่เสร็จ รอสักครู่แล้วลองใหม่');
    const c = document.createElement('canvas'); c.width = c.height = 48;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('เครื่องนี้อ่านสีจากรูปไม่ได้ (หน่วยความจำไม่พอ)');
    // only the part that shows (ครอป)
    ctx.drawImage(el, img.cropX ?? 0, img.cropY ?? 0, img.width ?? n.w, img.height ?? n.h, 0, 0, 48, 48);
    return mainColor(ctx.getImageData(0, 0, 48, 48).data);
  }
  function colorToPage(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    const list = chosen();
    if (list.length !== 1) return list.length ? 'ใช้สีได้ทีละชิ้น' : 'เลือกชิ้นงานก่อน';
    let hex: string | null;
    try { hex = colorOf(list[0]); }
    catch (e) { return 'อ่านสีจากชิ้นนี้ไม่ได้: ' + errText(e); }
    if (!hex) return 'ชิ้นนี้ไม่มีสีเดียวให้ใช้ (เช่น ไล่สี หรือโปร่งใส)';
    ed.setDocBg(hex); opts.onBgChanged();
    toast(`ใช้สี ${hex} เป็นสีพื้นหลังแล้ว (ทุกหน้าใช้สีเดียวกัน) เปลี่ยนได้ที่ เพิ่มเติม → สีพื้นหลังงาน`);
    return null;
  }

  /* ---------- ปรับขนาดหน้าให้พอดีกับที่เลือก ---------- */

  function fitPage(): string | null {
    { const no = cannotEdit(); if (no) return no; }
    if (ed.doc.video) return 'งานวิดีโอมีขนาดตายตัว (9:16 หรือ 1:1) ปรับขนาดหน้าไม่ได้';
    const list = chosen();
    if (!list.length) return 'เลือกชิ้นงานก่อน';
    const b = fitPageBox(loose(list, () => unionBox(list.map(boxOf))));
    const err = checkDocSize(b.w, b.h);
    if (err) return err;
    // an edit still in its pause is recorded first (as itself); then move everything on the page so the selection
    // starts at the page corner, and size the page: one undo step
    history.flushEdit();
    history.silently(() => {
      canvas.discardActiveObject();
      ed.userObjects().forEach(o => { o.set({ left: (o.left ?? 0) - b.x, top: (o.top ?? 0) - b.y }); o.setCoords(); });
    });
    const why = history.resizePages('this', { w: b.w, h: b.h });
    if (why) {
      ed.userObjects().forEach(o => { o.set({ left: (o.left ?? 0) + b.x, top: (o.top ?? 0) + b.y }); o.setCoords(); });
      canvas.requestRenderAll();
      return 'ปรับขนาดหน้าไม่ได้: ' + why;
    }
    canvas.requestRenderAll();
    toast(`ปรับหน้า ${history.currentPage() + 1} เป็น ${b.w} × ${b.h} px พอดีกับที่เลือกแล้ว (เลิกทำได้)`);
    return null;
  }

  /* ---------- ดาวน์โหลดที่เลือก ---------- */

  async function downloadChosen(): Promise<string | null> {
    const all = chosen().filter(o => o.visible !== false);
    // โน้ตแปะ are never in a file: say so instead of a blank picture (BUG-53)
    const list = all.filter(o => !(o as { note?: boolean }).note);
    if (!all.length) return 'เลือกชิ้นงานที่จะดาวน์โหลดก่อน';
    if (!list.length) return 'โน้ตแปะแสดงบนจอเท่านั้น ไม่อยู่ในไฟล์ที่ส่งออก (เลือกชิ้นงานอื่นแล้วดาวน์โหลดได้)';
    const objects = loose(list, () => list.map(plain));
    // a line's หัวลูกศร stick out of its own box (src/editor/arrows.ts): the picture includes them
    const b = fitPageBox(loose(list, () => unionBox(list.map(o => { const r = boxOf(o), m = arrowOverhang(o); return { x: r.x - m, y: r.y - m, w: r.w + 2 * m, h: r.h + 2 * m }; }))), 1);
    try {
      const r = await exportObjectsPng(objects, b);
      const name = list.length === 1 ? (labelOf(list[0]) || 'ชิ้นงาน') : `${ed.doc.name} ${list.length} ชิ้น`;
      download(r.blob, exportFileName(name, thStamp(new Date()), 'png'));
      toast(`ดาวน์โหลด PNG พื้นใส ${b.w} × ${b.h} px แล้ว${r.note ? ' · ' + r.note : ''}`);
      return null;
    } catch (e) { return 'ดาวน์โหลดไม่สำเร็จ: ' + errText(e); }
  }

  /* ---------- ข้อมูล ---------- */

  /** lines describing the chosen object (or the selection) */
  function info(o: Obj): string[] {
    const mm = (px: number) => (Math.round(pxToMm(px) * 10) / 10).toLocaleString('th-TH');
    const b = boxOf(o);
    const out = [
      `ขนาด ${Math.round(b.w)} × ${Math.round(b.h)} px (${mm(b.w)} × ${mm(b.h)} mm ที่ 300 DPI)`,
      `ตำแหน่ง ซ้าย ${Math.round(b.x)} · บน ${Math.round(b.y)} px` + (o.angle ? ` · หมุน ${Math.round(o.angle)}°` : '')
    ];
    if (o.type === 'image') {
      const n = naturalSize(o as fabric.Image);
      out.push(`ไฟล์รูป ${n.w} × ${n.h} px · ความละเอียดตอนพิมพ์ ${Math.round(effectivePpi(plain(o) as PlainObject))} ppi (ควร 300 ขึ้นไป)`);
      if (o.photoEdit) out.push('แต่งภาพไว้ (แสง/สี/ฟิลเตอร์)');
    }
    if (isThaiText(o)) out.push(`ฟอนต์ ${o.fontFamily} ${Math.round(o.fontSize ?? 0)} px · ${(o.text ?? '').length} ตัวอักษร`);
    if ((o.opacity ?? 1) < 1) out.push(`ความทึบ ${Math.round((o.opacity ?? 1) * 100)}%`);
    if (o.locked) out.push('ล็อกอยู่');
    return out;
  }

  /** every unlocked object on the page */
  function selectAll(): string | null {
    const list = ed.userObjects().filter(o => o.selectable !== false && o.visible !== false);
    if (!list.length) return 'หน้านี้ยังไม่มีชิ้นงานที่เลือกได้';
    canvas.discardActiveObject();
    canvas.setActiveObject(list.length > 1 ? new fabric.ActiveSelection(list, { canvas }) : list[0]);
    ed.paint(); canvas.requestRenderAll();
    return null;
  }

  /** forget the clipboard (signed out: the next person must not paste this account's pictures) */
  const clearClipboard = () => { clip = null; style = null; };

  return {
    clearClipboard, chosen, copy, paste, hasClip, duplicate, remove, lock, unlock, layer, align, pieces, distribute,
    groupState, group, ungroup, copyStyle, pasteStyle, styleToPaste, asBackground, colorToPage, fitPage, downloadChosen, info, selectAll
  };
}

export type ObjectActions = ReturnType<typeof createObjectActions>;
